import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, Subject, filter, fromEvent, map, merge } from 'rxjs';
import { IObservableStore } from './interfaces/observable-store.interface';

interface SessionEntry<T> {
  ttl?: string | Date | null;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class SessionService implements IObservableStore {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly memoryStorage = new Map<string, string>();
  private readonly keyChanges = new Map<string, Subject<unknown>>();

  public get<T>(key: string): T {
    const serializedValue = this.getStorage()?.getItem(key) ?? this.memoryStorage.get(key) ?? null;
    const obj = this.deserialize<SessionEntry<T>>(serializedValue);

    if (!obj) {
      return null as T;
    }

    if (obj.ttl != null) {
      const objDate = new Date(obj.ttl as Date).getTime();
      const date = new Date().getTime();

      if (date - objDate > 0) {
        this.clear(key);
        return null as T;
      }
    }
    return obj.data as T;
  }

  public set<T>(key: string, data: T, ttl?: Date): void {
    const payload: SessionEntry<T> = { ttl, data };
    const serializedValue = JSON.stringify(payload);
    const storage = this.getStorage();

    if (storage) {
      storage.setItem(key, serializedValue);
    } else {
      this.memoryStorage.set(key, serializedValue);
    }

    this.emitChange(key, data);
  }

  public clear(key?: string): void {
    const storage = this.getStorage();

    if (key) {
      if (storage) {
        storage.removeItem(key);
      } else {
        this.memoryStorage.delete(key);
      }

      this.emitChange(key, null);
      return;
    }

    if (storage) {
      storage.clear();
    } else {
      this.memoryStorage.clear();
    }

    this.keyChanges.forEach(subject => subject.next(null));
  }

  public onChange<T>(key: string): Observable<T> {
    const inTabChanges = this.getOrCreateSubject(key) as Subject<T>;
    const crossTabChanges = this.getStorageEvents<T>(key);
    return merge(inTabChanges.asObservable(), crossTabChanges);
  }

  private getStorage(): Storage | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    return this.document.defaultView?.sessionStorage ?? null;
  }

  private getOrCreateSubject(key: string): Subject<unknown> {
    const existingSubject = this.keyChanges.get(key);
    if (existingSubject) {
      return existingSubject;
    }

    const subject = new Subject<unknown>();
    this.keyChanges.set(key, subject);
    return subject;
  }

  private emitChange(key: string, value: unknown): void {
    this.getOrCreateSubject(key).next(value);
  }

  private getStorageEvents<T>(key: string): Observable<T> {
    const currentWindow = this.document.defaultView;
    if (!currentWindow) {
      return EMPTY;
    }

    return fromEvent<StorageEvent>(currentWindow, 'storage').pipe(
      filter(event => event.storageArea === currentWindow.sessionStorage && event.key === key),
      map(event => {
        const storedEntry = this.deserialize<SessionEntry<T>>(event.newValue);
        if (!storedEntry) {
          return null as T;
        }

        if (storedEntry.ttl != null) {
          const ttlDate = new Date(storedEntry.ttl).getTime();
          if (Date.now() - ttlDate > 0) {
            return null as T;
          }
        }

        return storedEntry.data;
      })
    );
  }

  private deserialize<T>(value: string | null): T {
    if (value == null) {
      return null as T;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return null as T;
    }
  }
}
